import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import "./Modal.css";

const USER_ID_THRESHOLD = 999;

function buildExportObject(options) {
  const guide = window.guide_obj;
  const { exportGuideInfo, exportPlaces, scheduleSelection, exportGroups, exportCategories, exportChapters } = options;

  const guide_info = exportGuideInfo
    ? {
        guide_name: guide.attributes?.guide_name ?? "",
        description: guide.attributes?.description ?? "",
        image: guide.attributes?.image ?? "",
        image_m: guide.attributes?.image_m ?? "",
        language: guide.attributes?.language ?? "",
        countries: guide.attributes?.countries ?? [],
        cities: guide.attributes?.cities ?? [],
        base_location: guide.attributes?.base_location ?? null,
        document: guide.document ?? "",
        custom_icons: (guide.custom_icons || []).map((ic) => ({
          id: ic.id, name: ic.name, poi_img: ic.poi_img, url: ic.url,
        })),
        // destinations' dateStart/dateEnd are Luxon DateTimes — JSON.stringify
        // calls their .toJSON() (== .toISO()) automatically, same convention
        // as Guide.js's own Firestore round-trip. No special encode needed.
        destinations: guide.destinations || [],
        aiPreferences: guide.aiPreferences || null,
        flights: guide.flights || [],
        guide_settings: guide.guide_settings || null,
        // Expense Tracking MVP (docs/competitive-analysis.md) — plain
        // arrays, no Luxon fields, so no special encode needed, same as
        // destinations/flights above.
        tripMembers: guide.tripMembers || [],
        expenses: guide.expenses || [],
      }
    : null;

  const places = exportPlaces
    ? (guide.places?.placesList || []).map((p) => ({
        ID: p.ID, Name: p.Name, Lat: p.Lat, Longi: p.Longi,
        Address: p.Address, City: p.City, State: p.State, Country: p.Country,
        Phone: p.Phone, Email: p.Email, Rating: p.Rating, Hlink: p.Hlink,
        Info: p.Info, Category: p.Category, Group: p.Group,
        Photo: p.Photo, Photo_m: p.Photo_m, PType: p.PType,
        Favorite: p.Favorite, Visited: p.Visited, MustSee: p.MustSee,
        BucketList: p.BucketList, Hidden: p.Hidden, Duration: p.Duration,
      }))
    : [];

  const selectedSchedules = Object.entries(scheduleSelection).filter(([, v]) => v).map(([k]) => k);
  const events = (guide.schedule?.events || [])
    .filter((e) => {
      const it = typeof e.Itinerary === "undefined" ? "Main" : e.Itinerary.replace(/%20/g, " ");
      return selectedSchedules.includes(it);
    })
    .map((e) => ({
      ID: e.ID, Subject: e.Subject,
      Start: e.Start ? (e.Start.toISO?.() ?? String(e.Start)) : "",
      End: e.End ? (e.End.toISO?.() ?? String(e.End)) : "",
      Places: e.Places, Itinerary: e.Itinerary, Info: e.Info,
    }));

  const groups = exportGroups
    ? (guide.groups || [])
        .filter((g) => parseInt(g.id, 10) > USER_ID_THRESHOLD)
        .map((g) => ({ id: g.id, name: g.name, poi_img: g.poi_img, schedule: g.schedule, mosaic: g.mosaic, image: g.image }))
    : [];

  const categories = exportCategories
    ? (guide.categories || [])
        .filter((c) => parseInt(c.id, 10) > USER_ID_THRESHOLD)
        .map((c) => ({ id: c.id, name: c.name, poi_img: c.poi_img }))
    : [];

  const chapters = exportChapters
    ? (guide.chapters || []).map((ch) => ({
        ID: ch.ID, Title: ch.Title,
        sections: (ch.sections || []).map((s) => ({
          ID: s.ID, Title: s.Title, html: s.html,
          PlaceIds: s.PlaceIds, Groups: s.Groups, Categories: s.Categories,
        })),
      }))
    : [];

  return { guide_info, places, events, groups, categories, chapters };
}

function csvEscape(val) {
  if (val == null) return "";
  const s = String(val).replace(/"/g, '""');
  return /[,"\n\r]/.test(s) ? `"${s}"` : s;
}

function rowsToCSV(headers, rows) {
  const lines = [headers.map(csvEscape).join(",")];
  rows.forEach((r) => lines.push(headers.map((h) => csvEscape(r[h])).join(",")));
  return lines.join("\n");
}

function downloadFile(content, filename, type) {
  const file = new Blob([content], { type });
  const element = document.createElement("a");
  element.href = URL.createObjectURL(file);
  element.download = filename;
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
}

function exportAsJSON(options) {
  const data = buildExportObject(options);
  const name = (window.guide_obj?.attributes?.guide_name || "trip").replace(/[^a-zA-Z0-9_-]/g, "_");
  downloadFile(JSON.stringify(data, null, 2), `${name}_trip.json`, "application/json");
}

function exportAsCSV(options) {
  const { guide_info, places, events, groups, categories, chapters } = buildExportObject(options);
  const parts = [];

  if (guide_info) {
    parts.push("=== GUIDE INFO ===");
    // These are nested objects/arrays-of-objects, not flat lists — join(";")
    // would mangle them into "[object Object]". Round-tripped as a JSON
    // string in the value column instead (parseCSV mirrors this on import).
    const structuredKeys = new Set(["base_location", "destinations", "aiPreferences", "flights", "guide_settings", "tripMembers", "expenses"]);
    const infoRows = Object.entries(guide_info)
      .filter(([k]) => k !== "custom_icons")
      .map(([k, v]) => ({
        key: k,
        value: structuredKeys.has(k)
          ? (v == null ? "" : JSON.stringify(v))
          : Array.isArray(v) ? v.join(";") : v == null ? "" : String(v),
      }));
    parts.push(rowsToCSV(["key", "value"], infoRows));
    if ((guide_info.custom_icons || []).length) {
      parts.push("", "=== CUSTOM ICONS ===");
      parts.push(rowsToCSV(["id", "name", "poi_img", "url"], guide_info.custom_icons));
    }
    parts.push("");
  }

  if (places.length) {
    parts.push("=== PLACES ===");
    parts.push(rowsToCSV(
      ["ID","Name","Lat","Longi","Address","City","State","Country","Phone","Email","Rating","Hlink","Category","Group","PType","Favorite","Visited","MustSee","BucketList","Hidden","Duration"],
      places.map((p) => ({ ...p, Category: (p.Category || []).join(";"), Group: (p.Group || []).join(";") }))
    ));
    parts.push("");
  }

  if (events.length) {
    parts.push("=== EVENTS ===");
    parts.push(rowsToCSV(["ID","Subject","Start","End","Places","Itinerary","Info"], events));
    parts.push("");
  }

  if (groups.length) {
    parts.push("=== GROUPS (user-defined) ===");
    parts.push(rowsToCSV(["id","name","poi_img","schedule","mosaic","image"], groups));
    parts.push("");
  }

  if (categories.length) {
    parts.push("=== CATEGORIES (user-defined) ===");
    parts.push(rowsToCSV(["id","name","poi_img"], categories));
    parts.push("");
  }

  const sectionRows = [];
  chapters.forEach((ch) => {
    (ch.sections || []).forEach((s) => {
      sectionRows.push({
        chapter_title: ch.Title, chapter_ID: ch.ID,
        section_ID: s.ID, section_title: s.Title,
        PlaceIds: (s.PlaceIds || []).join(";"),
        Groups: (s.Groups || []).join(";"),
        Categories: (s.Categories || []).join(";"),
      });
    });
  });
  if (sectionRows.length) {
    parts.push("=== CHAPTERS & SECTIONS ===");
    parts.push(rowsToCSV(["chapter_title","chapter_ID","section_ID","section_title","PlaceIds","Groups","Categories"], sectionRows));
  }

  const name = (window.guide_obj?.attributes?.guide_name || "trip").replace(/[^a-zA-Z0-9_-]/g, "_");
  downloadFile(parts.join("\n"), `${name}_trip.csv`, "text/csv");
}

// ── component ──────────────────────────────────────────────────────────────────

function initScheduleSelection() {
  const scheduleNames = window.guide_obj?.schedule?.geScheduleNames?.() ?? [{ label: "Main", value: "Main" }];
  const sel = {};
  scheduleNames.forEach((s) => { sel[s.value] = true; });
  return sel;
}

export default function ExportTripModal({ show, onHide }) {
  const [exportGuideInfo, setExportGuideInfo] = useState(true);
  const [exportPlaces, setExportPlaces] = useState(true);
  const [scheduleSelection, setScheduleSelection] = useState(() => initScheduleSelection());
  const [exportGroups, setExportGroups] = useState(true);
  const [exportCategories, setExportCategories] = useState(true);
  const [exportChapters, setExportChapters] = useState(true);

  useEffect(() => {
    if (show) setScheduleSelection(initScheduleSelection());
  }, [show]);

  if (!show) return null;

  const guide = window.guide_obj;
  const guideName = guide?.attributes?.guide_name ?? "";
  const scheduleNames = guide?.schedule?.geScheduleNames?.() ?? [{ label: "Main", value: "Main" }];
  const placesCount = guide?.places?.placesList?.length ?? 0;
  const userGroupsCount = (guide?.groups || []).filter((g) => parseInt(g.id, 10) > USER_ID_THRESHOLD).length;
  const userCatsCount = (guide?.categories || []).filter((c) => parseInt(c.id, 10) > USER_ID_THRESHOLD).length;
  const chaptersCount = (guide?.chapters || []).length;
  const hasCustomIcons = (guide?.custom_icons || []).length > 0;

  const options = { exportGuideInfo, exportPlaces, scheduleSelection, exportGroups, exportCategories, exportChapters };

  const eventsForSchedule = (scheduleName) =>
    (guide?.schedule?.events || []).filter((e) => {
      const it = typeof e.Itinerary === "undefined" ? "Main" : e.Itinerary.replace(/%20/g, " ");
      return it === scheduleName;
    }).length;

  const toggleSchedule = (name, value) =>
    setScheduleSelection((prev) => ({ ...prev, [name]: value }));

  const handleExportJSON = () => { exportAsJSON(options); onHide(); };
  const handleExportCSV = () => { exportAsCSV(options); onHide(); };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Export Trip{guideName ? ` — ${guideName}` : ""}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: "1rem" }}>

        {/* Guide section */}
        <div className="export-section-header">Guide</div>
        <ToggleRow
          label={hasCustomIcons ? "Guide info & custom icons" : "Guide info"}
          checked={exportGuideInfo}
          onChange={setExportGuideInfo}
        />
        <hr className="export-divider" />
        <ToggleRow
          label="Places"
          count={placesCount}
          checked={exportPlaces}
          onChange={setExportPlaces}
        />

        {/* Schedules section */}
        <div className="export-section-header" style={{ marginTop: "1rem" }}>Schedules</div>
        {scheduleNames.map((s) => (
          <ToggleRow
            key={s.value}
            label={s.label}
            count={eventsForSchedule(s.value)}
            checked={scheduleSelection[s.value] ?? true}
            onChange={(v) => toggleSchedule(s.value, v)}
          />
        ))}

        {/* Organisation section */}
        <div className="export-section-header" style={{ marginTop: "1rem" }}>Organisation</div>
        <ToggleRow
          label="User groups"
          count={userGroupsCount}
          checked={exportGroups}
          onChange={setExportGroups}
        />
        <hr className="export-divider" />
        <ToggleRow
          label="User categories"
          count={userCatsCount}
          checked={exportCategories}
          onChange={setExportCategories}
        />

        {/* Content section */}
        <div className="export-section-header" style={{ marginTop: "1rem" }}>Content</div>
        <ToggleRow
          label="Chapters & sections"
          count={chaptersCount}
          checked={exportChapters}
          onChange={setExportChapters}
        />

        <p style={{ fontSize: "0.75rem", color: "#888", marginTop: "1rem", marginBottom: 0 }}>
          Built-in groups and categories are not exported — they are part of the app.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-secondary btn-sm" onClick={onHide}>Cancel</button>
        <button className="btn btn-outline-primary btn-sm" onClick={handleExportCSV}>Export as CSV</button>
        <button className="btn btn-primary btn-sm" onClick={handleExportJSON}>Export as JSON</button>
      </Modal.Footer>
    </Modal>
  );
}

function ToggleRow({ label, count, checked, onChange }) {
  return (
    <div className="export-toggle-row">
      <span className="export-toggle-label">
        {label}
        {count != null && <span className="export-count-pill">{count}</span>}
      </span>
      <div className="form-check form-switch mb-0">
        <input
          className="form-check-input"
          type="checkbox"
          role="switch"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          style={{ cursor: "pointer" }}
        />
      </div>
    </div>
  );
}
